/** Telegram WebApp helpers — no native TG confirm/popup/alert for game actions. */

export function tg() {
  return typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
}

/**
 * Bypass telegram-web-app.js version gates. Some Android / fork clients report
 * an old webAppVersion so addToHomeScreen/shareToStory throw WebAppMethodUnsupported,
 * while the native menu still supports the feature. postEvent talks to the client directly.
 */
export function postTelegramEvent(eventType, eventData) {
  if (typeof window === 'undefined' || !eventType) return false;
  try {
    const wv = window.Telegram?.WebView;
    if (wv?.postEvent) {
      if (eventData === undefined) wv.postEvent(eventType);
      else wv.postEvent(eventType, false, eventData);
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  try {
    if (window.TelegramWebviewProxy?.postEvent) {
      window.TelegramWebviewProxy.postEvent(
        eventType,
        eventData === undefined ? '' : JSON.stringify(eventData)
      );
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

export function isVersionAtLeast(v) {
  try {
    return Boolean(tg()?.isVersionAtLeast?.(v));
  } catch {
    return false;
  }
}

/** Desktop / web clients — home shortcut & stories APIs are mobile-only. */
export function isDesktopTelegram() {
  const p = String(tg()?.platform || '').toLowerCase();
  // UA backup: some Android WebViews mis-report platform as "web"/"weba"
  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent || '';
    if (/Android|iPhone|iPad|iPod/i.test(ua)) return false;
  }
  return ['tdesktop', 'web', 'weba', 'webk', 'macos'].includes(p);
}

export function isMobileClient() {
  if (!isDesktopTelegram()) {
    const p = String(tg()?.platform || '').toLowerCase();
    if (['android', 'android_x', 'ios', 'iphone', 'ipad'].includes(p)) return true;
  }
  if (typeof navigator !== 'undefined') {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
  }
  return false;
}

/**
 * Home-screen API availability. Never hard-block mobile on missing methods —
 * older Android builds lack Bot API 8.0; we still offer a manual confirm path.
 */
export function canAddToHomeScreen() {
  if (isDesktopTelegram()) return false;
  return true;
}

export function hasHomeScreenApi() {
  // Method may exist but throw on version gate — we can still postEvent.
  return Boolean(tg()?.addToHomeScreen) || Boolean(window.Telegram?.WebView?.postEvent);
}

export function canShareStory() {
  // Prefer live bridge — version strings often under-report on Android forks.
  if (tg()?.shareToStory) return true;
  if (window.Telegram?.WebView?.postEvent && isMobileClient()) return true;
  return false;
}

export function setChromeColors(header, bg) {
  const w = tg();
  if (!w) return;
  try {
    if (header) w.setHeaderColor?.(header);
    if (bg) w.setBackgroundColor?.(bg);
    if (w.setBottomBarColor && bg) w.setBottomBarColor(bg);
  } catch (_) {
    /* ignore */
  }
}

export function ensureImmersive() {
  const w = tg();
  if (!w) return;
  try {
    w.ready?.();
    w.expand?.();
    // Never requestFullscreen — clips UI under status/nav bars
    w.disableVerticalSwipes?.();
  } catch (_) {
    /* ignore */
  }
  applyTelegramSafeArea();
}

/** Map Telegram safe-area insets into CSS vars (env() is unreliable in TG WebView). */
export function applyTelegramSafeArea() {
  if (typeof document === 'undefined') return;
  try {
    const w = tg();
    const sa = w?.safeAreaInset || {};
    const csa = w?.contentSafeAreaInset || {};
    const top = Math.max(0, Number(sa.top) || 0) + Math.max(0, Number(csa.top) || 0);
    const bottom = Math.max(0, Number(sa.bottom) || 0) + Math.max(0, Number(csa.bottom) || 0);
    const root = document.documentElement;
    root.style.setProperty('--tg-safe-top', `${top}px`);
    root.style.setProperty('--tg-safe-bottom', `${bottom}px`);
  } catch (_) {
    /* ignore */
  }
}

/**
 * @deprecated Phone "lite" visual downgrade removed — UI matches desktop.
 * Kept as a no-op so any stale imports do not break; clears leftover class.
 */
export function applyPerfMode() {
  if (typeof document === 'undefined') return false;
  document.body.classList.remove('perf-lite');
  applyTelegramSafeArea();
  return false;
}

export function setClosingConfirmation(enabled) {
  const w = tg();
  if (!w) return;
  try {
    // Retention prompt disabled — always allow free close
    w.disableClosingConfirmation?.();
    if (enabled) {
      /* intentionally ignored */
    }
  } catch (_) {
    /* ignore */
  }
}

export function addToHomeScreen() {
  if (isDesktopTelegram()) return false;
  // 1) Direct bridge — skips JS version gate that blocks many Android clients
  if (postTelegramEvent('web_app_add_to_home_screen')) return true;
  // 2) Official helper (may throw WebAppMethodUnsupported)
  const w = tg();
  if (!w?.addToHomeScreen) return false;
  try {
    w.addToHomeScreen();
    return true;
  } catch {
    return false;
  }
}

/** Quick status probe — never blocks longer than maxWaitMs. */
export function checkHomeScreenStatus(maxWaitMs = 800) {
  return new Promise((resolve) => {
    const w = tg();
    let done = false;
    const finish = (status) => {
      if (done) return;
      done = true;
      try {
        w?.offEvent?.('homeScreenChecked', onChecked);
      } catch (_) {
        /* ignore */
      }
      resolve(status || 'unknown');
    };
    const onChecked = (payload) => {
      const status = typeof payload === 'string' ? payload : payload?.status;
      finish(status || 'unknown');
    };

    try {
      w?.onEvent?.('homeScreenChecked', onChecked);
    } catch (_) {
      /* ignore */
    }

    // Prefer direct event (bypasses version gate)
    const posted = postTelegramEvent('web_app_check_home_screen');
    if (!posted && w?.checkHomeScreenStatus) {
      try {
        w.checkHomeScreenStatus((status) => finish(status || 'unknown'));
      } catch (_) {
        finish('no_api');
        return;
      }
    } else if (!posted && !w?.checkHomeScreenStatus) {
      finish('no_api');
      return;
    }
    setTimeout(() => finish('unknown'), maxWaitMs);
  });
}

/**
 * Prompt Telegram to add shortcut and wait for success / timeout.
 * Returns 'added' | 'missed' | 'unsupported' | 'unknown' | 'no_api'
 */
export function requestHomeScreen(timeoutMs = 8000) {
  return new Promise((resolve) => {
    const w = tg();
    let settled = false;
    const finish = (status) => {
      if (settled) return;
      settled = true;
      try {
        w?.offEvent?.('homeScreenAdded', onAdded);
      } catch (_) {
        /* ignore */
      }
      clearInterval(poll);
      clearTimeout(timer);
      resolve(status);
    };
    const onAdded = () => finish('added');
    try {
      w?.onEvent?.('homeScreenAdded', onAdded);
    } catch (_) {
      /* ignore */
    }

    const ok = addToHomeScreen();
    if (!ok) {
      finish('no_api');
      return;
    }

    const poll = setInterval(async () => {
      try {
        const s = await checkHomeScreenStatus(400);
        if (s === 'added') finish('added');
      } catch (_) {
        /* ignore */
      }
    }, 500);

    const timer = setTimeout(async () => {
      const s = await checkHomeScreenStatus(400);
      finish(s === 'added' ? 'added' : s === 'no_api' ? 'no_api' : 'missed');
    }, timeoutMs);
  });
}

export function shareToStory(mediaUrl, params = {}) {
  if (!mediaUrl) return false;
  try {
    const a = document.createElement('a');
    a.href = mediaUrl;
    if (a.protocol !== 'http:' && a.protocol !== 'https:') return false;

    const shareParams = { media_url: a.href };
    if (params.text) {
      const text = String(params.text).trim().slice(0, 2048);
      if (text) shareParams.text = text;
    }
    if (params.widget_link?.url) {
      const link = document.createElement('a');
      link.href = params.widget_link.url;
      if (link.protocol === 'http:' || link.protocol === 'https:') {
        shareParams.widget_link = { url: link.href };
        const name = String(params.widget_link.name || '').trim().slice(0, 48);
        if (name) shareParams.widget_link.name = name;
      }
    }

    // Direct bridge first — skips version gate that throws on many Android builds
    if (postTelegramEvent('web_app_share_to_story', shareParams)) return true;

    const w = tg();
    if (!w?.shareToStory) return false;
    try {
      w.shareToStory(mediaUrl, params);
      return true;
    } catch (_) {
      try {
        w.shareToStory(mediaUrl, params?.text ? { text: params.text } : undefined);
        return true;
      } catch (__) {
        try {
          w.shareToStory(mediaUrl);
          return true;
        } catch (___) {
          return false;
        }
      }
    }
  } catch (_) {
    return false;
  }
}

export function switchInlineQuery(query = '', chatTypes = ['users']) {
  const w = tg();
  if (!w?.switchInlineQuery) return false;
  try {
    w.switchInlineQuery(query, chatTypes);
    return true;
  } catch {
    return false;
  }
}

/** Soft in-app toast replacement for rare one-liners (shake bonus etc). */
export function tgAlert(message) {
  return Promise.resolve();
}

export const LEAGUE_CHROME = {
  bronze: { header: '#2a1810', bg: '#1a0f0a' },
  silver: { header: '#241c16', bg: '#16110e' },
  gold: { header: '#2a2008', bg: '#1a1405' },
  platinum: { header: '#2a1c10', bg: '#1a120a' },
  diamond: { header: '#2a1808', bg: '#1a1005' },
  master: { header: '#2a0e08', bg: '#160806' },
  default: { header: '#07040a', bg: '#07040a' },
};

export function applyLeagueChrome(league) {
  const c = LEAGUE_CHROME[league] || LEAGUE_CHROME.default;
  setChromeColors(c.header, c.bg);
}
